import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Modal,
  Button,
  MenuItem,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { useAddToneMutation, useLazyGetToneByIdQuery, useUpdateToneMutation } from '../services/toneConfig';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { showError, showToast } from '../constants/toasts';
import { BUDGET_TIER_OPTIONS } from '../utils/enums';
import AudioUpload from '../components/AudioUpload';
import { API_URL } from '../constants/url';

interface Props {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>
  mode: 'add' | 'edit';
  editId?: number | null;
  onSuccess?: () => void;
}

type ToneFormValues = {
  budget_tier: number;
  audio_tone: string;
};

export default function AddEditToneConfig({ open, onClose, setOpen, mode, editId = null, onSuccess }: Props) {
  const title = mode === 'add' ? 'Add Alert Tone' : 'Edit Alert Tone';
  const { id } = useParams();
  const [addTone] = useAddToneMutation();
  const [updateTone] = useUpdateToneMutation();
  const [getById] = useLazyGetToneByIdQuery();
  const [currentAudioFile, setCurrentAudioFile] = useState<string>("");

  const handleAudioUploadSuccess = (audioData: any) => {
    formik.setFieldValue("audio_tone", audioData.id);
    setCurrentAudioFile(""); 
  };

  const formik = useFormik<ToneFormValues>({
    initialValues: {
      budget_tier: 0,
      audio_tone: "",
    },
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      const trimmed = {
        budget_tier: values.budget_tier,
        audio_tone: values.audio_tone?.toString().trim(),
      };

      if (trimmed.audio_tone === "" || trimmed.budget_tier === undefined) {
        showError("All fields are required");
        setSubmitting(false);
        return;
      }

      try {
        const toneData = {
          audio_tone: parseInt(trimmed.audio_tone),
          budget_tier: trimmed.budget_tier,
        };

        if (mode === 'edit' && editId) {
          const res: any = await updateTone({ body: toneData, id: editId }).unwrap();
          if (res?.status === 200 || res?.status === 201) {
            showToast("Tone updated successfully");
            setOpen(false);
            onSuccess?.();
          }
        } else {
          const res: any = await addTone({ body: toneData }).unwrap();
          if (res?.status === 200 || res?.status === 201) {
            showToast("Tone added successfully");
            setOpen(false);
            onSuccess?.();
          }
        }
      } catch (e: any) {
        showError(e?.data?.message || "Something went wrong");
      } finally {
        setSubmitting(false);
      }
    },
  });

 
  useEffect(() => {
    const fetchDetail = async () => {
      if (!editId || mode !== 'edit') return;
      try {
        const response: any = await getById({ id: editId }).unwrap();
        if (response?.status === 200) {
          const item = Array.isArray(response.data) ? response.data[0] : response.data;
          if (item) {
            formik.setValues({
              budget_tier: item?.budget_tier ?? 0,
              audio_tone: item?.audio_tone?.toString() ?? "",
            });
            setCurrentAudioFile(item?.audio_tone?.toString() ?? "");
          }
        }
      } catch (e: any) {
        showError(e?.data?.message || "Failed to load tone details");
      }
    };
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, mode]);

  return (
    <Modal
      className="modal alertConfig_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{title}</h2>
            <div className="btn-close" onClick={() => setOpen(false)}>
              <CloseIcon />
            </div>
          </div>

          <form className="form" onSubmit={formik.handleSubmit}>
            <div className="control_group">
              <label>Select Budget Tier</label>
              <Select
                fullWidth
                displayEmpty
                value={formik.values.budget_tier}
                onChange={(e) => formik.setFieldValue("budget_tier", e.target.value)}
                onBlur={() => formik.setFieldTouched("budget_tier", true)}
              >
                <MenuItem disabled value="">
                  Select Budget Tier
                </MenuItem>
                {BUDGET_TIER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.budget_tier && formik.values.budget_tier === undefined ? (
                <h6 className="err_msg">Budget Tier is required</h6>
              ) : ""}
            </div>

            <div className="control_group">
              <label>Upload Tone</label>
              <AudioUpload
                onUploadSuccess={handleAudioUploadSuccess}
                disabled={formik.isSubmitting}
                className="tone-upload"
              />
              {formik.touched.audio_tone && !formik.values.audio_tone ? (
                <h6 className="err_msg">Audio file is required</h6>
              ) : ""}
              {mode === 'edit' && currentAudioFile && !formik.values.audio_tone && (
                <div style={{ marginTop: "8px" }}>
                  <p style={{ color: "#666", fontSize: "12px", marginBottom: "8px" }}>
                    Current audio file:
                  </p>
                  <audio controls style={{ width: '100%', maxWidth: '300px' }}>
                    <source src={`${API_URL}/media/${currentAudioFile}`} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
              {formik.values.audio_tone && (
                <p style={{ color: "#1976d2", fontSize: "12px", marginTop: "8px" }}>
                  ✓ Audio file uploaded successfully
                </p>
              )}
            </div>

            <div className="footer_btn_flex">
              <Button 
                type="button"
                className="btn btn_grey" 
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="btn btn_primary" 
                disabled={formik.isSubmitting}
                onClick={() => {
                  formik.setTouched({ budget_tier: true, audio_tone: true });
                  formik.handleSubmit();
                }}
              >
                {formik.isSubmitting ? "Processing..." : mode === 'edit' ? "Update" : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
